import { BUILD_NUMBER, platform } from '@/utils/buildInfo';

/**
 * The terminal "this build can no longer talk to the server" latch.
 *
 * The iOS app ships a frozen copy of the web client inside the .ipa — Capacitor
 * serves it from the app package and never loads hi-aleph.com as a remote page
 * — so when the API makes a breaking change the installed app simply stops
 * working, with nothing on screen to say why. That is what happened at the
 * schema 1.1 cutover. This converts that silence into "Update Aleph".
 *
 * It lives outside React, and the screen it drives renders above the router
 * with no API or auth dependency, because a block screen that needs the app to
 * work in order to display is useless in exactly the situation it exists for.
 *
 * Note what this cannot do: it only ever helps builds that ship with it. It is
 * a ratchet for the *next* breaking change, never the current one.
 */

/**
 * Where to send someone whose build is too old. The server sends its own copy
 * so the link can be corrected without a release; this is the fallback for a
 * 426 whose body we could not read.
 */
export const APP_STORE_URL = 'https://apps.apple.com/app/id6796461524';

export const DEFAULT_GATE_MESSAGE =
  'Update Aleph to keep reading. This version can no longer load your books.';

export interface ClientGateRequirement {
  /** The oldest build the server will still serve, when it told us. */
  minBuild: number | null;
  /** This build, for the support conversation that follows a confused report. */
  yourBuild: number;
  storeUrl: string;
  message: string;
}

let requirement: ClientGateRequirement | null = null;
const listeners = new Set<(blocked: ClientGateRequirement) => void>();

/** The current latch, for a component mounting after the trip. */
export const clientGateRequirement = (): ClientGateRequirement | null => requirement;

/**
 * Record that the server has refused this build, and return whether that
 * actually gated the client.
 *
 * Returns false — leaving the caller to handle the response as it would any
 * other failure — in the two cases where blocking would be wrong:
 *
 * - on web, where the service worker self-updates and a stuck gate would mean
 *   locking someone out of a page that a reload would have fixed. The server
 *   does not gate web either; this is the second lock on that door.
 * - once already tripped, so the first answer stands and a later, different
 *   payload cannot rewrite the screen under the reader.
 */
export const tripClientGate = (
  partial: Partial<Omit<ClientGateRequirement, 'yourBuild'>>,
): boolean => {
  if (platform() !== 'ios' || requirement) {
    return false;
  }
  requirement = {
    minBuild: partial.minBuild ?? null,
    yourBuild: BUILD_NUMBER,
    storeUrl: partial.storeUrl || APP_STORE_URL,
    message: partial.message || DEFAULT_GATE_MESSAGE,
  };
  console.warn(
    `[clientGate] Server refused build ${BUILD_NUMBER}`
    + `${requirement.minBuild === null ? '' : `; it requires ${requirement.minBuild}`}.`,
  );
  const blocked = requirement;
  listeners.forEach((listener) => listener(blocked));
  return true;
};

export const subscribeToClientGate = (
  listener: (blocked: ClientGateRequirement) => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Test seam. The latch is deliberately terminal in the running app. */
export const resetClientGateForTests = (): void => {
  requirement = null;
  listeners.clear();
};

/**
 * Read a 426 body into a requirement.
 *
 * The status code is the instruction; the body only decorates it. A 426 whose
 * JSON is missing or shaped unexpectedly still blocks, with our own copy and
 * link — the server has already said this build cannot be served, and guessing
 * otherwise would put the user back in front of a reader that cannot load.
 */
export const parseUpgradeRequired = async (
  response: Response,
): Promise<Partial<Omit<ClientGateRequirement, 'yourBuild'>>> => {
  try {
    const body = (await response.json()) as { detail?: Record<string, unknown> };
    const detail = body?.detail;
    if (!detail || typeof detail !== 'object') {
      return {};
    }
    const minBuild = detail.min_build;
    const storeUrl = detail.store_url;
    const message = detail.message;
    return {
      minBuild: typeof minBuild === 'number' ? minBuild : null,
      storeUrl: typeof storeUrl === 'string' ? storeUrl : undefined,
      message: typeof message === 'string' ? message : undefined,
    };
  } catch {
    return {};
  }
};
